import { useState } from "react"

import { useTranslation } from "react-i18next"

import type { CriticalDate, CriticalDateUserRow, SourceRef, SourceSnippet } from "../types"

import { Calendar, Pencil, Plus, Trash2, Undo2 } from "@/components/icons"
import { Badge } from "@/components/ui/badge"
import { Button } from "@/components/ui/button"
import {
  Dialog,
  DialogBody,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog"
import { Input } from "@/components/ui/input"
import { Label } from "@/components/ui/label"
import { cn } from "@/lib/utils"

import {
  formatDeadline,
  fromDateAndTimeInputs,
  toDateInputValue,
  toTimeInputValue,
} from "../utils/deadline"

const URGENCY_CONFIG: Record<CriticalDate["urgency"], { labelKey: string; className: string }> = {
  kritisk: {
    labelKey: "tender.status.codes.kritisk",
    className: "bg-destructive/10 text-destructive border-destructive",
  },
  varning: {
    labelKey: "tender.status.codes.varning",
    className: "bg-warning/10 text-warning-foreground border-warning",
  },
  normal: {
    labelKey: "tender.status.codes.normal",
    className: "bg-muted/60 text-muted-foreground border-border",
  },
}

type Props = {
  criticalDates: CriticalDate[]
  userRows: CriticalDateUserRow[]
  extractedIds: string[]
  canEdit: boolean
  isUpdating: boolean
  onApply: (payload: { removedIds: string[]; userRows: CriticalDateUserRow[] }) => Promise<void>
  removedIds: string[]
  getSnippetDocumentUrl: (s: CriticalDate["snippet"]) => string | undefined
  openDocumentPreview: (source: SourceRef | SourceSnippet) => void
  SnippetPopover: React.ComponentType<{
    documentUrl?: string
    reasoning?: string
    snippet: NonNullable<CriticalDate["snippet"]>
    onOpenPreview?: () => void
  }>
}

export function CriticalDatesTabEditor({
  criticalDates,
  userRows: userRowsProp,
  extractedIds,
  canEdit,
  isUpdating,
  onApply,
  removedIds,
  getSnippetDocumentUrl,
  openDocumentPreview,
  SnippetPopover,
}: Props) {
  const { t, i18n } = useTranslation()

  const [dialogOpen, setDialogOpen] = useState(false)
  const [isNew, setIsNew] = useState(false)
  const [editingId, setEditingId] = useState<string | null>(null)
  const [draftEvent, setDraftEvent] = useState("")
  const [draftDate, setDraftDate] = useState("")
  const [draftTime, setDraftTime] = useState("")
  const [draftDateText, setDraftDateText] = useState("")

  const openEdit = (row: CriticalDate) => {
    setIsNew(false)
    setEditingId(row.id)
    setDraftEvent(row.event)
    setDraftDate(toDateInputValue(row.date))
    setDraftTime(toTimeInputValue(row.date))
    setDraftDateText(row.dateText ?? "")
    setDialogOpen(true)
  }

  const openCreate = () => {
    setIsNew(true)
    setEditingId(null)
    setDraftEvent("")
    setDraftDate("")
    setDraftTime("")
    setDraftDateText("")
    setDialogOpen(true)
  }

  const persist = async (nextUser: CriticalDateUserRow[]) => {
    await onApply({ removedIds, userRows: nextUser })
  }

  const handleDialogSave = async () => {
    const ev = draftEvent.trim()
    if (!ev) return
    const dateIso = fromDateAndTimeInputs(draftDate, draftTime)
    const dt = draftDateText.trim() || null

    if (isNew) {
      const id = crypto.randomUUID()
      const nextRow: CriticalDateUserRow = {
        id,
        event: ev,
        date: dateIso,
        dateText: dt,
        reasoning: null,
      }
      await persist([...userRowsProp, nextRow])
    } else if (editingId) {
      const nextUpsert: CriticalDateUserRow = {
        id: editingId,
        event: ev,
        date: dateIso,
        dateText: dt,
        reasoning: null,
      }
      const others = userRowsProp.filter((r) => r.id !== editingId)
      await persist([...others, nextUpsert])
    }
    setDialogOpen(false)
  }

  const removeManualRow = async (id: string) => {
    await persist(userRowsProp.filter((r) => r.id !== id))
  }

  const removeExtractedRow = async (id: string) => {
    await onApply({ removedIds: [...removedIds, id], userRows: userRowsProp })
  }

  const resetOverride = async (id: string) => {
    await persist(userRowsProp.filter((r) => r.id !== id))
  }

  const isManualOnly = (id: string) =>
    userRowsProp.some((r) => r.id === id) && !extractedIds.includes(id)

  return (
    <>
      {canEdit ? (
        <div className="mb-3 flex justify-end">
          <Button
            className="gap-2"
            disabled={isUpdating}
            size="sm"
            type="button"
            variant="outline"
            onClick={openCreate}
          >
            <Plus className="size-3.5" />
            {t("tender.extraction.criticalDates.addRow")}
          </Button>
        </div>
      ) : null}

      {criticalDates.length > 0 ? (
        <div className="divide-border divide-y overflow-hidden rounded-lg border">
          <div className="bg-muted/40 text-muted-foreground grid grid-cols-[1fr_116px_80px_88px_minmax(0,1fr)_minmax(0,120px)] gap-3 px-5 py-3 text-xs font-medium tracking-wide uppercase">
            <span>{t("tender.extraction.tableHeaders.event")}</span>
            <span>{t("tender.extraction.tableHeaders.date")}</span>
            <span>{t("tender.extraction.tableHeaders.daysLeft")}</span>
            <span>{t("tender.extraction.tableHeaders.status")}</span>
            <span>{t("tender.extraction.tableHeaders.source")}</span>
            <span className="text-right">{t("tender.extraction.criticalDates.actionsColumn")}</span>
          </div>
          {criticalDates
            .slice()
            .sort((a, b) => Number(a.daysRemaining) - Number(b.daysRemaining))
            .map((date) => {
              const urgency = URGENCY_CONFIG[date.urgency]
              const origin = date.rowOrigin ?? "ai"
              return (
                <div
                  key={date.id}
                  className="grid grid-cols-[1fr_116px_80px_88px_minmax(0,1fr)_minmax(0,120px)] items-center gap-3 px-5 py-4"
                >
                  <div className="flex min-w-0 items-center gap-2">
                    <Calendar className="text-muted-foreground size-4 shrink-0" />
                    <div className="min-w-0">
                      <p className="min-w-0 text-sm">{date.event}</p>
                      {origin === "manual" ? (
                        <span className="text-muted-foreground text-[10px]">
                          {t("tender.extraction.criticalDates.rowOriginManual")}
                        </span>
                      ) : null}
                    </div>
                  </div>
                  <p className={cn("text-sm font-medium", date.date && "tabular-nums")}>
                    {date.date ? (
                      formatDeadline(date.date, i18n.language, { dateOnly: true })
                    ) : (
                      <span className="text-muted-foreground italic">{date.dateText ?? "—"}</span>
                    )}
                  </p>
                  <p
                    className={cn(
                      "text-sm font-semibold tabular-nums",
                      date.urgency === "kritisk" && "text-destructive",
                      date.urgency === "varning" && "text-warning"
                    )}
                  >
                    {date.daysRemaining != null
                      ? t("tender.extraction.daysRemainingShort", {
                          count: date.daysRemaining,
                        })
                      : "—"}
                  </p>
                  <Badge
                    className={cn("w-fit border font-normal", urgency.className)}
                    variant="outline"
                  >
                    {t(urgency.labelKey)}
                  </Badge>
                  <div className="min-w-0">
                    {date.snippet ? (
                      <SnippetPopover
                        documentUrl={getSnippetDocumentUrl(date.snippet)}
                        reasoning={date.reasoning}
                        snippet={date.snippet}
                        onOpenPreview={
                          date.snippet.kind === "file"
                            ? () => openDocumentPreview(date.snippet!)
                            : undefined
                        }
                      />
                    ) : (
                      <span className="text-muted-foreground text-xs">—</span>
                    )}
                  </div>
                  <div className="flex flex-wrap justify-end gap-1">
                    {canEdit ? (
                      <>
                        <Button
                          aria-label={t("tender.extraction.criticalDates.editAria")}
                          className="size-8"
                          disabled={isUpdating}
                          size="icon"
                          type="button"
                          variant="ghost"
                          onClick={() => openEdit(date)}
                        >
                          <Pencil className="size-3.5" />
                        </Button>
                        {origin === "manual" && extractedIds.includes(date.id) ? (
                          <Button
                            aria-label={t("tender.extraction.criticalDates.resetAria")}
                            className="size-8"
                            disabled={isUpdating}
                            size="icon"
                            type="button"
                            variant="ghost"
                            onClick={() => void resetOverride(date.id)}
                          >
                            <Undo2 className="size-3.5" />
                          </Button>
                        ) : null}
                        {origin === "manual" && isManualOnly(date.id) ? (
                          <Button
                            aria-label={t("tender.extraction.criticalDates.deleteAria")}
                            className="size-8"
                            disabled={isUpdating}
                            size="icon"
                            type="button"
                            variant="ghost"
                            onClick={() => void removeManualRow(date.id)}
                          >
                            <Trash2 className="size-3.5" />
                          </Button>
                        ) : null}
                        {origin === "ai" ? (
                          <Button
                            aria-label={t("tender.extraction.criticalDates.removeAria")}
                            className="size-8"
                            disabled={isUpdating}
                            size="icon"
                            type="button"
                            variant="ghost"
                            onClick={() => void removeExtractedRow(date.id)}
                          >
                            <Trash2 className="size-3.5" />
                          </Button>
                        ) : null}
                      </>
                    ) : null}
                  </div>
                </div>
              )
            })}
        </div>
      ) : null}

      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle>
              {isNew
                ? t("tender.extraction.criticalDates.dialogAddTitle")
                : t("tender.extraction.criticalDates.dialogEditTitle")}
            </DialogTitle>
          </DialogHeader>
          <DialogBody className="space-y-3">
            <div className="space-y-1.5">
              <Label htmlFor="cd-event">{t("tender.extraction.criticalDates.fieldEvent")}</Label>
              <Input
                id="cd-event"
                value={draftEvent}
                onChange={(e) => setDraftEvent(e.target.value)}
              />
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div className="space-y-1.5">
                <Label htmlFor="cd-date">{t("tender.extraction.criticalDates.fieldDate")}</Label>
                <Input
                  id="cd-date"
                  type="date"
                  value={draftDate}
                  onChange={(e) => setDraftDate(e.target.value)}
                />
              </div>
              <div className="space-y-1.5">
                <Label htmlFor="cd-time">{t("tender.extraction.criticalDates.fieldTime")}</Label>
                <Input
                  id="cd-time"
                  type="time"
                  value={draftTime}
                  onChange={(e) => setDraftTime(e.target.value)}
                />
              </div>
            </div>
            <div className="space-y-1.5">
              <Label htmlFor="cd-datetext">
                {t("tender.extraction.criticalDates.fieldDateText")}
              </Label>
              <Input
                id="cd-datetext"
                placeholder={t("tender.extraction.criticalDates.fieldDateTextPlaceholder")}
                value={draftDateText}
                onChange={(e) => setDraftDateText(e.target.value)}
              />
            </div>
          </DialogBody>
          <DialogFooter>
            <Button type="button" variant="outline" onClick={() => setDialogOpen(false)}>
              {t("tender.quickStart.cancel")}
            </Button>
            <Button
              disabled={!draftEvent.trim() || isUpdating}
              type="button"
              onClick={() => void handleDialogSave()}
            >
              {t("tender.extraction.criticalDates.save")}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  )
}
